// ============================================================================
// BOOKING SERVICE
// ============================================================================
// Business logic and API calls for Booking module

import { get, post, put, del, downloadFile } from '../client/axiosClient';
import { bookingEndpoints } from '../endpoints/booking.endpoints';
import { buildEndpointWithQuery } from '../endpoints/base.endpoints';
import { dayKey } from 'utils/calendarDate';
import type {
  ApiResponse,
  PaginatedResponse,
  EmptyResponse,
  ID,
} from '../types';
import type {
  Booking,
  BookingAdjustmentRequest,
  BookingFull,
  ChangeBookingSourceRequest,
  RateWorksheetDiscount,
  CreateBookingRequest,
  UpdateBookingRequest,
  BookingSearchRequest,
  DayGuestListResponse,
  DayGuestGroup,
} from '../types/booking.types';
import type { ChangeSettlementRequest, SettlementChangePreview, SettlementChoice, SettlementMode } from '../types/settlement.types';

/**
 * Booking Service Class
 */
class BookingService {
  /**
   * Get all bookings (paginated)
   */
  async getAll(params?: BookingSearchRequest): Promise<PaginatedResponse<Booking>> {
    const endpoint = params
      ? buildEndpointWithQuery('/Booking', params)
      : bookingEndpoints.list();

    const response = await get<any>(endpoint);
    const raw = response.data;
    const pagedResult = raw.data ?? {};

    return {
      success: raw.success,
      message: raw.message,
      data: (pagedResult.items ?? []).map((b: any) => ({ ...b, id: b.bookingId })),
      pagination: {
        currentPage: pagedResult.page ?? 1,
        pageSize: pagedResult.pageSize ?? 20,
        totalPages: pagedResult.totalPages ?? 0,
        totalItems: pagedResult.totalCount ?? 0,
        hasNextPage: pagedResult.hasNext ?? false,
        hasPreviousPage: pagedResult.hasPrevious ?? false,
      },
    };
  }

  /**
   * Get booking by ID
   */
  async getById(id: ID): Promise<ApiResponse<Booking>> {
    const response = await get<ApiResponse<any>>(bookingEndpoints.getById(id));
    const raw = response.data;
    if (raw.data) raw.data = { ...raw.data, id: raw.data.bookingId };
    return raw;
  }

  /**
   * Get full booking (header + details + guests)
   */
  async getFullBooking(id: ID): Promise<ApiResponse<BookingFull>> {
    const response = await get<ApiResponse<any>>(bookingEndpoints.getFullBooking(id));
    const raw = response.data;
    if (raw.data?.booking) raw.data.booking = { ...raw.data.booking, id: raw.data.booking.bookingId };
    return raw;
  }

  /**
   * Create new booking
   */
  async create(data: CreateBookingRequest): Promise<ApiResponse<Booking>> {
    const response = await post<ApiResponse<any>>(
      bookingEndpoints.create(),
      data
    );
    const raw = response.data;
    if (raw.data) raw.data = { ...raw.data, id: raw.data.bookingId };
    return raw;
  }

  /**
   * Update booking
   */
  async update(id: ID, data: UpdateBookingRequest): Promise<ApiResponse<Booking>> {
    const response = await put<ApiResponse<Booking>>(
      bookingEndpoints.update(id),
      data
    );
    return response.data;
  }

  /**
   * Rate Worksheet — replaces the booking's rate adjustments and recomputes
   * money surgically. Works on in-house and departed bookings the full edit
   * refuses; lines, dates, rooms, inventory and status are untouched.
   */
  async applyRateWorksheet(
    id: ID,
    adjustments: BookingAdjustmentRequest[],
    discount?: RateWorksheetDiscount,
  ): Promise<Booking> {
    const response = await put<ApiResponse<Booking>>(
      bookingEndpoints.rateWorksheet(id),
      { adjustments, discount },
    );
    if (!response.data?.data) throw new Error(response.data?.message || 'Rate update failed.');
    return response.data.data;
  }

  /**
   * Surgical source/channel correction (wrong OTA / agent / corporate).
   * Never re-prices; allowed on any non-released status.
   */
  async changeSource(id: ID, payload: ChangeBookingSourceRequest): Promise<Booking> {
    const response = await put<ApiResponse<Booking>>(
      bookingEndpoints.changeSource(id),
      payload,
    );
    if (!response.data?.data) throw new Error(response.data?.message || 'Source correction failed.');
    return response.data.data;
  }

  /** What changing the booking's settlement to `mode` would move (PMS slice 3b). */
  async previewSettlementChange(id: ID, mode?: SettlementChoice): Promise<SettlementChangePreview> {
    const response = await get<ApiResponse<SettlementChangePreview>>(
      buildEndpointWithQuery(`/Booking/${id}/settlement/preview`, mode ? { mode } : {}),
    );
    if (!response.data?.data) throw new Error(response.data?.message || 'Could not read the settlement.');
    return response.data.data;
  }

  /** In-house "Change settlement" (PMS slice 3b). */
  async changeSettlement(id: ID, payload: ChangeSettlementRequest): Promise<Booking> {
    const response = await put<ApiResponse<Booking>>(bookingEndpoints.changeSettlement(id), payload);
    if (!response.data?.data) throw new Error(response.data?.message || 'Settlement change failed.');
    return response.data.data;
  }

  /**
   * Delete booking
   */
  async delete(id: ID, rowVersion: number): Promise<EmptyResponse> {
    const response = await del<EmptyResponse>(bookingEndpoints.delete(id), {
      params: { rowVersion },
    });
    return response.data;
  }

  /**
   * Download booking confirmation PDF
   */
  async downloadConfirmationPdf(id: ID, bookingNumber?: string): Promise<void> {
    const fileName = bookingNumber ? `Booking-${bookingNumber}.pdf` : `Booking-${id}.pdf`;
    await downloadFile(bookingEndpoints.confirmationPdf(id), fileName);
  }

  /**
   * Fetch booking confirmation PDF as a Blob (no auto-download). Used by
   * the in-app preview modal — caller creates an object URL for an iframe.
   */
  async getConfirmationPdfBlob(id: ID): Promise<Blob> {
    const { axiosClient } = await import('../client/axiosClient');
    const response = await axiosClient.get(bookingEndpoints.confirmationPdf(id), {
      responseType: 'blob',
    });
    return response.data as Blob;
  }

  /**
   * Transition a TENTATIVE booking to BLOCKED with an auto-cancel deadline.
   * The backend caps expiresAt at max_block_hold_hours (per-property config).
   */
  async block(id: ID, expiresAt: string, remarks?: string): Promise<ApiResponse<Booking>> {
    const response = await post<ApiResponse<any>>(
      bookingEndpoints.block(id),
      { expiresAt, remarks }
    );
    const raw = response.data;
    if (raw.data) raw.data = { ...raw.data, id: raw.data.bookingId };
    return raw;
  }

  /**
   * Update expires_at on an already-BLOCKED booking.
   */
  async updateBlockExpiry(id: ID, expiresAt: string): Promise<ApiResponse<Booking>> {
    const response = await post<ApiResponse<any>>(
      bookingEndpoints.updateBlockExpiry(id),
      { expiresAt }
    );
    const raw = response.data;
    if (raw.data) raw.data = { ...raw.data, id: raw.data.bookingId };
    return raw;
  }

  /**
   * One-click extend on a BLOCKED booking. Server reads policy and extends
   * the expiry by `extension_hours`, clamped at `max_block_hold_hours`. Rejects
   * with 409 when extensions are disabled, max-extensions is reached, or the
   * booking is already at the cap.
   */
  async extendBlock(id: ID): Promise<ApiResponse<Booking>> {
    const response = await post<ApiResponse<any>>(
      bookingEndpoints.extendBlock(id),
      {}
    );
    const raw = response.data;
    if (raw.data) raw.data = { ...raw.data, id: raw.data.bookingId };
    return raw;
  }

  /**
   * Manually confirm a BLOCKED booking — clears expires_at and flips to CONFIRMED.
   */
  async confirmBlocked(id: ID): Promise<ApiResponse<Booking>> {
    const response = await post<ApiResponse<any>>(
      bookingEndpoints.confirmBlocked(id),
      {}
    );
    const raw = response.data;
    if (raw.data) raw.data = { ...raw.data, id: raw.data.bookingId };
    return raw;
  }

  /**
   * Cashier-initiated request to extend the advance due date on a TENTATIVE
   * unpaid booking. Creates an ADVANCE_OVERRIDE approval row.
   */
  async requestDueDateExtension(id: ID, proposedDueDate: string, justification: string): Promise<ApiResponse<Booking>> {
    const response = await post<ApiResponse<any>>(
      bookingEndpoints.requestDueDateExtension(id),
      { proposedDueDate, justification }
    );
    const raw = response.data;
    if (raw.data) raw.data = { ...raw.data, id: raw.data.bookingId };
    return raw;
  }

  /**
   * Who is arriving, staying over and departing on one date — the Booking
   * Worksheet's Day View. NOT the list filtered by date: `dateType=checkin`
   * returns arrivals only and cannot return the staying-over guests.
   *
   * Rows are ordinary bookings, so the Day View reuses the worksheet's columns
   * and row actions unchanged — hence the same `bookingId` → `id` mapping the
   * grid needs everywhere else.
   */
  async getDayGuestList(
    propertyId: ID,
    date: string,
    q?: string,
    includeHolds = true
  ): Promise<ApiResponse<DayGuestListResponse>> {
    const endpoint = buildEndpointWithQuery('/Booking/day-guest-list', {
      propertyId,
      date,
      q: q || undefined,
      includeHolds,
    });
    const response = await get<ApiResponse<any>>(endpoint);
    const raw = response.data;

    const mapGroup = (g: any): DayGuestGroup => ({
      total: g?.total ?? 0,
      items: (g?.items ?? []).map((b: any) => ({ ...b, id: b.bookingId })),
    });

    if (raw.data) {
      raw.data = {
        ...raw.data,
        // Same normalisation as the occupancy horizon — see dayKey().
        date: dayKey(raw.data.date),
        arriving: mapGroup(raw.data.arriving),
        stayingOver: mapGroup(raw.data.stayingOver),
        departing: mapGroup(raw.data.departing),
      };
    }
    return raw;
  }
}

/**
 * Export singleton instance
 */
export const bookingService = new BookingService();
export default bookingService;
